import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { RangePaginationState, RangePagination, paginate } from 'components/basic/Pagination';
import StageActionToolbar from '../generic/StageActionToolbar';
import { stagingPageUpdate, stagingPageCopyMappings, StagingPageCopyMappingsItem } from '../generic/Abstractions'
import '../../../../../styles/Icons.scss';
import { ActionTypeModel, ActionTypeRefModel, ActionTypesClient, ConversionClient, ConversionModel, ExecutionsClient } from '../../../../../client/ApiClient';
import { DataCell, DataRow, DataTable } from '../../../../basic/DataTable';
import DataListInput from '../../../../basic/DataListInput';
import { RowStatus, AssignRowStatus } from '../generic';
import { ApplicationState } from '../../../../../store';
import { connect } from 'react-redux';
import { store } from '../../../../../AppContext';
import { enqueueNotification } from '../../../../../store/Notifications';

interface ImageStagingPageArgs {
    conversionId: string,
}

type ImageStagingPageProps = RouteComponentProps<ImageStagingPageArgs>;

interface ImageStagingPageState {
    filter: string;
    items: (ActionTypeModel
        & StagingPageCopyMappingsItem)[];
    destinationCodes: ActionTypeRefModel[];
    pagination: RangePaginationState;
    currentConversion: ConversionModel | undefined;
    buttonLock: boolean;
}

class ImageStagingPage extends React.Component<ImageStagingPageProps, ImageStagingPageState>{
    private client: ActionTypesClient = new ActionTypesClient();
    private executions: ExecutionsClient = new ExecutionsClient();
    private readonly copyMappings = stagingPageCopyMappings(this);

    constructor(props: ImageStagingPageProps) {
        super(props)
        this.state = {
            filter: '',
            items: [],
            destinationCodes: [],
            pagination: {
                pageSize: 25,
                pageNumber: 0,
            },
            currentConversion: undefined,
            buttonLock: false,
        }
    }

    componentDidMount() {
        this.getItems();
    }

    getItems() {
        this.client.getAll(this.props.match.params.conversionId, '', 'image')
            .then((response) => {
                this.setState({
                    items: response!
                }, () => {
                    this.buttonLock();
                })
            })
            .catch(error => { })
        this.client.getRef(this.props.match.params.conversionId)
            .then(response => {
                if (response != null) {
                    this.setState({
                        destinationCodes: response,
                    })
                }
            })
            .catch(error => { })
    }

    buttonLock = () => {
        const conversionId = this.props.match.params.conversionId;
        if (this.state.currentConversion && this.state.currentConversion!.dateCompleted === undefined) {
            this.executions.listExecutionsAllTierBoolean(conversionId)
                .then((response) => {
                    if (response) {
                        this.setState({ buttonLock: response.isLocked })
                    }
                })
                .catch(error => { })
        }
    }

    handleChange = (e: any) => {
        const dc = e.value;
        const id = Number(e.target.name);

        const items = this.state.items;
        const i = this.state.items.findIndex(v => v.id === id);
        items[i].destinationCode = dc;

        this.setState({ items });
        this.put(ActionTypeModel.fromJS(items[i]))
    }

    handleCheckboxChange = (id: number, e: { target: { name: any, checked: any } }) => {
        const items = this.state.items.map(
            v => v.id === id
                ? new ActionTypeModel({
                    ...v,
                    [e.target.name]: e.target.checked,
                })
                : v)
        this.setState({ items });
        this.put(ActionTypeModel.fromJS(items.find(v => v.id === id)!))
    }

    put(entity: ActionTypeModel) {
        entity.destinationCode = entity.destinationCode === null ? "" : entity.destinationCode
        const conversionId = this.props.match.params.conversionId;
        this.client.put(conversionId, entity.id!, entity) 
            .then(() => {
                store.dispatch(enqueueNotification({ type: 'success', message: "Image " + entity.sourceCode + " was saved" }))
                this.getItems();
            })
            .catch(error => {
                console.log(error)
            })
    }

    actionTypesRefs = (): any[] => {
        const arr = [];
        const actionTypesRefs = this.state.destinationCodes;
        let index = 0;
        for (const [, actionTypesRef] of actionTypesRefs.entries()) {
            arr.push(<option
                key={index}
                data-value={actionTypesRef.name}
            >
                {actionTypesRef.isHide === 'Y' ? actionTypesRef.name + ' - Inactive' : actionTypesRef.name}
            </option>)
            index = index + 1;
        }
        return arr;
    }

    lockButton = (lock: boolean) => {
        this.setState({ buttonLock: lock })
    }

    public render() {
        const conversionId = this.props.match.params.conversionId;
        const items = this.state.items
            .map(AssignRowStatus)
        return (
            <>
                <StageActionToolbar
                    conversionId={conversionId}
                    sectionId='images'
                    tierGate='tier4'
                    filter={this.state.filter}
                    handleCopyMapping={this.copyMappings.submitMappings}
                    copyMap={true}
                    lockButton={this.lockButton}
                    noClear={true}
                    getItems={() => this.getItems()}
                />
                <DataTable>
                    <thead>
                        <tr className='nonFullWidthRow'>
                            <th>Source Code</th>
                            <th>Source Description<i className="fa fa-sort"></i></th>
                            <th className='checkbox-column'>Exclude<i className="fa fa-sort"></i></th>
                            <th>Destination Code<i className="fa fa-sort"></i></th>
                            <th className='checkbox-column'>Mapped </th>
                            <th className='checkbox-column'>Auto-Mapped</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            paginate(items, this.state.pagination, (item, index) => (
                                <DataRow id={item.id.toString()} key={item.id}>
                                    <DataCell>{item.sourceCode}</DataCell>
                                    <DataCell>{item.sourceDescription}</DataCell>
                                    <DataCell className='checkbox-column'>
                                        <input className='custom-checkbox'
                                            type='checkbox'
                                            name='isDrop'
                                            checked={item.isDrop}
                                            onChange={e => this.handleCheckboxChange(item.id, e)}
                                            disabled={this.state.buttonLock}
                                        />
                                    </DataCell>
                                    <DataCell>
                                        <DataListInput
                                            id={`referenceId - ${index}`}
                                            name={item.id.toString()}
                                            value={item.destinationCode ? item.destinationCode : ""}
                                            onValueChange={e => this.handleChange(e)}
                                            disabled={this.state.buttonLock}
                                        >
                                            <option data-value='' />
                                            {this.actionTypesRefs()}
                                        </DataListInput>
                                    </DataCell>
                                    <DataCell className='checkbox-column' itemProp={item.mapped === RowStatus.NotMapped ? 'unmapped' : item.mapped === RowStatus.Mapped ? 'mapped' : 'dropped'}>
                                        {item.mapped === RowStatus.NotMapped ? < span className='mapped' itemProp='unmapped'>&#x274C;</span> : item.mapped === RowStatus.Mapped ? < span className='mapped' itemProp='mapped'>&#x2705;</span> : < span className='mapped' itemProp='dropped'>&#x2796;</span>}
                                    </DataCell>
                                    <DataCell
                                        className='checkbox-column'
                                        itemProp={!item.isUserMapped && item.isMapped ? 'auto-mapped' : ''}
                                    >
                                        {!item.isUserMapped && item.isMapped ? < span className='mapped' itemProp='auto-mapped'>&#x2705;</span> : ''}
                                    </DataCell>
                                </DataRow>
                            ))
                        }
                    </tbody>
                </DataTable>
                <RangePagination
                    pageNumber={this.state.pagination.pageNumber}
                    pageSize={this.state.pagination.pageSize}
                    recordsCount={0}
                    onChangeIndex={index => this.setState({
                        pagination: {
                            ...this.state.pagination,
                            pageNumber: index,
                        }
                    })}
                />
            </>
        );
    }
}

export default connect(
    (state: ApplicationState) => {
        return {
            currentUser: state.authentication && state.authentication.currentUser
        }
    },
)(ImageStagingPage);